import React, { useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  Container,
  Menu,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Fade,
  Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import CalculateIcon from "@mui/icons-material/Calculate";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import EmailIcon from "@mui/icons-material/Email";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useNavigate, useLocation } from "react-router-dom";
import DatabaseExcelExportModal from "./DatabaseExcelExportModal";

const primaryNavItems = [
  { name: "Home", to: "/", icon: <HomeIcon /> },
  { name: "Gold Loan", to: "/gold-loan", icon: <MonetizationOnIcon /> },
  { name: "Vehicle Loan", to: "/vehicle-loan", icon: <DirectionsCarIcon /> },
  { name: "EMI Calculator", to: "/emi", icon: <CalculateIcon /> },
  { name: "Loan Products", to: "/loan-selection", icon: <AccountBalanceIcon /> },
  { name: "Contact", to: "/contact", icon: <ContactSupportIcon /> },
];

function ResponsiveAppBar() {
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openExportModal, setOpenExportModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const checkLoginStatus = () => {
      const user = sessionStorage.getItem("user");
      setIsLoggedIn(!!user);
    };

    checkLoginStatus();
    window.addEventListener("storage", checkLoginStatus);

    return () => {
      window.removeEventListener("storage", checkLoginStatus);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  function handleExportExcelClick() {
    setOpenExportModal(true);
    handleCloseUserMenu();
  }

  const settings = isLoggedIn
    ? [
        { name: "Home", icon: <HomeIcon />, action: handleHomeClick },
        { name: "Export Database (Excel)", icon: <FileDownloadIcon />, action: handleExportExcelClick },
        { name: "Logout", icon: <LogoutIcon />, action: handleLogout },
      ]
    : [
        { name: "Home", icon: <HomeIcon />, action: handleHomeClick },
        { name: "Login", icon: <LoginIcon />, action: handleLoginClick },
      ];

  function handleLoginClick() {
    navigate("/commonUserLogin");
    handleCloseUserMenu();
  }

  function handleLogout() {
    sessionStorage.clear();
    localStorage.clear();
    setIsLoggedIn(false);
    navigate("/", { replace: true });
    handleCloseUserMenu();
    window.location.reload();
  }

  function handleHomeClick() {
    navigate("/");
    handleCloseUserMenu();
  }

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const isActive = (path) => {
    if (path === "/" && location.pathname !== "/") return false;
    return location.pathname === path;
  };

  const DrawerList = (
    <Box
      sx={{
        width: 300,
        height: "100%",
        background: "#ffffff",
        color: "#1e293b",
        display: "flex",
        flexDirection: "column",
      }}
      role="presentation"
    >
      {/* Drawer Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2.5,
          borderBottom: "1px solid rgba(147, 96, 151, 0.12)",
          background: "linear-gradient(135deg, rgba(119, 200, 199, 0.08) 0%, rgba(147, 96, 151, 0.08) 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <img
            src="/mainlogo.png"
            alt="Logo"
            style={{
              height: "42px",
              width: "auto",
              objectFit: "contain",
              filter: "drop-shadow(0 2px 6px rgba(119, 200, 199, 0.3))",
            }}
          />
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 700,
                color: "#1e293b",
                letterSpacing: "0.5px",
                lineHeight: 1.1,
                fontSize: "1.1rem",
              }}
            >
              VELAVAN
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700,
                color: "#936097",
                letterSpacing: "1.5px",
                fontSize: "0.62rem",
                display: "block",
              }}
            >
              FINANCE PVT
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={toggleDrawer(false)}
          sx={{
            color: "#64748b",
            "&:hover": {
              backgroundColor: "rgba(147, 96, 151, 0.1)",
              color: "#936097",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Trust pill */}
      <Box sx={{ px: 2.5, pt: 2 }}>
        <Chip
          label="✓ RBI Compliant Financial Services"
          size="small"
          sx={{
            width: "100%",
            background: "rgba(119, 200, 199, 0.15)",
            color: "#0f766e",
            border: "1px solid rgba(119, 200, 199, 0.3)",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      </Box>

      {/* Nav list */}
      <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
        <List sx={{ pt: 0 }}>
          {primaryNavItems.map((item) => {
            const active = isActive(item.to);
            return (
              <ListItem key={item.name} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.to)}
                  sx={{
                    borderRadius: "12px",
                    px: 2,
                    py: 1.25,
                    backgroundColor: active
                      ? "linear-gradient(135deg, rgba(119, 200, 199, 0.18) 0%, rgba(147, 96, 151, 0.12) 100%)"
                      : "transparent",
                    background: active
                      ? "linear-gradient(135deg, rgba(119, 200, 199, 0.18) 0%, rgba(147, 96, 151, 0.12) 100%)"
                      : "transparent",
                    border: active
                      ? "1px solid rgba(147, 96, 151, 0.25)"
                      : "1px solid transparent",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(119, 200, 199, 0.1)",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: active ? "#936097" : "#64748b",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontFamily: "'Plus Jakarta Sans', 'Poppins', sans-serif",
                      fontWeight: active ? 700 : 500,
                      color: active ? "#936097" : "#334155",
                      fontSize: "0.95rem",
                    }}
                  />
                  {active && (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: "#77c8c7",
                        boxShadow: "0 0 8px #77c8c7",
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Drawer Footer / Support CTA */}
      <Box
        sx={{
          mt: "auto",
          p: 2.5,
          borderTop: "1px solid rgba(147, 96, 151, 0.12)",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            p: 1.5,
            borderRadius: "10px",
            background: "rgba(119, 200, 199, 0.12)",
            border: "1px solid rgba(119, 200, 199, 0.2)",
          }}
        >
          <EmailIcon sx={{ color: "#936097", fontSize: 22 }} />
          <Box>
            <Typography variant="caption" sx={{ color: "#64748b", display: "block", fontSize: "0.7rem" }}>
              Email Support Desk
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#936097", fontSize: "0.8rem" }}>
              poovarasan@velavanfinance.com
            </Typography>
          </Box>
        </Box>

        {!isLoggedIn ? (
          <Button
            onClick={() => handleNavigation("/commonUserLogin")}
            variant="contained"
            fullWidth
            startIcon={<LoginIcon />}
            sx={{
              background: "linear-gradient(135deg, #936097 0%, #77c8c7 100%)",
              color: "#ffffff",
              fontWeight: 600,
              py: 1.2,
              borderRadius: "10px",
              boxShadow: "0 4px 14px rgba(147, 96, 151, 0.25)",
              textTransform: "none",
              "&:hover": {
                background: "linear-gradient(135deg, #7e4d82 0%, #5eb8b7 100%)",
              },
            }}
          >
            Customer / Staff Login
          </Button>
        ) : (
          <Button
            onClick={handleLogout}
            variant="outlined"
            fullWidth
            startIcon={<LogoutIcon />}
            sx={{
              borderColor: "#936097",
              color: "#936097",
              fontWeight: 600,
              py: 1,
              borderRadius: "10px",
              textTransform: "none",
              "&:hover": {
                borderColor: "#7e4d82",
                backgroundColor: "rgba(147, 96, 151, 0.08)",
              },
            }}
          >
            Logout Session
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={scrolled ? 4 : 0}
        sx={{
          backgroundColor: scrolled
            ? "rgba(255, 255, 255, 0.95)"
            : "rgba(255, 255, 255, 0.90)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(147, 96, 151, 0.12)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: scrolled
            ? "0 10px 30px -10px rgba(0, 0, 0, 0.08), 0 0 20px rgba(119, 200, 199, 0.08)"
            : "0 2px 10px rgba(0, 0, 0, 0.02)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: "68px", md: "78px" } }}>
            {/* Mobile Hamburger Menu */}
            <Box sx={{ display: { xs: "flex", md: "none" }, mr: 1.5 }}>
              <IconButton
                size="medium"
                onClick={toggleDrawer(true)}
                sx={{
                  color: "#1e293b",
                  backgroundColor: "rgba(119, 200, 199, 0.12)",
                  borderRadius: "10px",
                  p: 1,
                  "&:hover": {
                    backgroundColor: "rgba(147, 96, 151, 0.15)",
                    color: "#936097",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Brand Logo & Name - Mobile */}
            <Box
              onClick={() => navigate("/")}
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                flexGrow: 1,
                cursor: "pointer",
                textDecoration: "none",
                gap: 1.2,
              }}
            >
              <img
                src="/mainlogo.png"
                alt="Velavan Finance"
                style={{
                  height: "44px",
                  width: "auto",
                  objectFit: "contain",
                  filter: "drop-shadow(0 2px 6px rgba(119, 200, 199, 0.3))",
                }}
              />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: 700,
                    color: "#1e293b",
                    letterSpacing: "0.5px",
                    lineHeight: 1.1,
                    fontSize: "1.1rem",
                  }}
                >
                  VELAVAN
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 700,
                    color: "#936097",
                    letterSpacing: "1.5px",
                    fontSize: "0.62rem",
                    display: "block",
                  }}
                >
                  FINANCE PVT
                </Typography>
              </Box>
            </Box>

            {/* Brand Logo & Name - Desktop */}
            <Box
              onClick={() => navigate("/")}
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                cursor: "pointer",
                textDecoration: "none",
                mr: 4,
                gap: 1.5,
                transition: "transform 0.25s ease",
                "&:hover": {
                  transform: "scale(1.02)",
                },
              }}
            >
              <img
                src="/mainlogo.png"
                alt="Velavan Finance Logo"
                style={{
                  height: "56px",
                  width: "auto",
                  objectFit: "contain",
                  filter: "drop-shadow(0 4px 12px rgba(119, 200, 199, 0.35))",
                }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: 700,
                    color: "#1e293b",
                    letterSpacing: "0.8px",
                    lineHeight: 1.05,
                    fontSize: "1.6rem",
                    background: "linear-gradient(90deg, #1e293b 40%, #936097 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  VELAVAN FINANCE
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 700,
                    color: "#77c8c7",
                    letterSpacing: "2.5px",
                    fontSize: "0.7rem",
                    display: "block",
                    textTransform: "uppercase",
                  }}
                >
                  Empowering Financial Trust
                </Typography>
              </Box>
            </Box>

            {/* Desktop Navigation Links */}
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              {primaryNavItems.map((item) => {
                const active = isActive(item.to);
                return (
                  <Button
                    key={item.name}
                    onClick={() => navigate(item.to)}
                    sx={{
                      color: active ? "#936097" : "#475569",
                      fontFamily: "'Plus Jakarta Sans', 'Poppins', sans-serif",
                      fontWeight: active ? 700 : 500,
                      fontSize: "0.92rem",
                      px: 1.8,
                      py: 0.8,
                      borderRadius: "10px",
                      textTransform: "none",
                      position: "relative",
                      backgroundColor: active
                        ? "rgba(147, 96, 151, 0.10)"
                        : "transparent",
                      border: active
                        ? "1px solid rgba(147, 96, 151, 0.25)"
                        : "1px solid transparent",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        backgroundColor: "rgba(119, 200, 199, 0.12)",
                        color: "#936097",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    {item.name}
                  </Button>
                );
              })}
            </Box>

            {/* Right Action: Login / User Menu */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {!isLoggedIn ? (
                <Button
                  onClick={() => navigate("/commonUserLogin")}
                  variant="contained"
                  startIcon={<LoginIcon />}
                  sx={{
                    background: "linear-gradient(135deg, #936097 0%, #77c8c7 100%)",
                    color: "#ffffff",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    textTransform: "none",
                    px: { xs: 1.8, md: 2.6 },
                    py: { xs: 0.7, md: 1 },
                    borderRadius: "10px",
                    boxShadow: "0 4px 16px rgba(147, 96, 151, 0.25)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "linear-gradient(135deg, #7e4d82 0%, #5eb8b7 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(147, 96, 151, 0.35)",
                    },
                  }}
                >
                  Login
                </Button>
              ) : (
                <>
                  <Tooltip title="Account menu" arrow>
                    <IconButton
                      onClick={handleOpenUserMenu}
                      sx={{
                        p: 0.5,
                        border: "2px solid #77c8c7",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.08)",
                          boxShadow: "0 0 14px rgba(119, 200, 199, 0.4)",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#936097",
                          width: 36,
                          height: 36,
                          fontSize: "1rem",
                          fontWeight: 700,
                          color: "#ffffff",
                        }}
                      >
                        V
                      </Avatar>
                    </IconButton>
                  </Tooltip>

                  <Fade in={Boolean(anchorElUser)}>
                    <Menu
                      sx={{ mt: "50px" }}
                      anchorEl={anchorElUser}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      open={Boolean(anchorElUser)}
                      onClose={handleCloseUserMenu}
                      PaperProps={{
                        sx: {
                          borderRadius: "14px",
                          boxShadow: "0 15px 40px rgba(0,0,0,0.12)",
                          background: "#ffffff",
                          border: "1px solid rgba(147, 96, 151, 0.15)",
                          minWidth: 180,
                          p: 0.5,
                          "& .MuiMenuItem-root": {
                            px: 2,
                            py: 1.2,
                            my: 0.3,
                            borderRadius: "8px",
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            color: "#1e293b",
                            fontSize: "0.9rem",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "rgba(119, 200, 199, 0.15)",
                              color: "#936097",
                            },
                          },
                        },
                      }}
                    >
                      {settings.map((setting) => (
                        <MenuItem
                          key={setting.name}
                          onClick={setting.action}
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            alignItems: "center",
                          }}
                        >
                          {React.cloneElement(setting.icon, {
                            sx: { fontSize: 20, color: "#936097" },
                          })}
                          <Typography
                            sx={{
                              fontFamily: "'Plus Jakarta Sans', sans-serif",
                              fontWeight: 600,
                            }}
                          >
                            {setting.name}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Menu>
                  </Fade>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>

        {/* Mobile Slide-in Drawer */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
          PaperProps={{
            sx: {
              border: "none",
              boxShadow: "8px 0 30px rgba(0,0,0,0.12)",
            },
          }}
        >
          {DrawerList}
        </Drawer>
      </AppBar>
      {/* Spacer to prevent content from hiding under fixed AppBar */}
      <Box sx={{ height: { xs: "68px", md: "78px" } }} />

      {/* Global Database Excel Export Modal */}
      <DatabaseExcelExportModal
        open={openExportModal}
        onClose={() => setOpenExportModal(false)}
      />
    </>
  );
}

export default ResponsiveAppBar;